import { Button, Grid, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { Field, Form } from 'react-final-form';
import { Column, RightDrawer, Row, Spacer } from '../../components';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { SubscriptionData, SubscriptionRest } from '../../api/apis/SubscriptionApi';
import { SubscriptionCompartments, subscriptionStorageKey } from '../../data/subscription';
import { IntervalTypeEnum, PricebookBundle } from '../../models/PricebookData';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ApiKeys } from '../../api/apis/ApiKeys';
import { useLoading } from '../../hooks/useLoading';
import LicenseAtAGlance from './LicenseAtAGlance';
import { Bold } from '../../components/Bold';
import { pluralize } from '../../helpers/utilityFunctions';
import LicensePaymentDetails from './LicensePaymentDetails';
import { LicenseAgreementApi } from '../../api/apis/LicenseAgreementApi';
import { AddLicenses, DecreaseLicenses } from '../../data/subscription/mutations';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { OrganizationData } from '../../models/OrganizationData';
import { OrganizationMetricsData } from '../../api/apis/OrgPricebookLicenseMetricsApi';
import useTermsOfServiceDocs from '../../hooks/useTermsOfServiceDocs';
import { usePricebookSelections } from '../../hooks/usePricebookOptions';

interface PurchaseLicensesDrawerProps {
  open: boolean;
  onClose: () => void;
  pricebook: PricebookBundle;
}

const PurchaseLicensesDrawer: React.FC<PurchaseLicensesDrawerProps> = ({
  open,
  onClose,
  pricebook,
}) => {
  const navigate = useNavigate();
  const appStorage = useAppStorage();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const termsOfServiceDocs = useTermsOfServiceDocs();

  const [activeSubscription, setActiveSubscription] = useState<SubscriptionData>();
  const [invoiceDate, setInvoiceDate] = useState<string>('');

  const licenseAgreementApi = useService<LicenseAgreementApi>(ApiKeys.LicenseAgreement);
  const addLicenses = useMutation(new AddLicenses());
  const decreaseLicenses = useMutation(new DecreaseLicenses());

  const pricebookSelections = usePricebookSelections() ?? [];
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const selectedOrg = useObservable(
    organizationDataCache.observe$<OrganizationData>('organization'),
  );
  const orgMetrics = useObservable(
    organizationDataCache.observe$<OrganizationMetricsData[]>('allMetrics'),
  );
  const licenseMetric = useMemo(() => {
    if (selectedOrg?.id && orgMetrics && pricebook.pricebook.id) {
      const metrics = orgMetrics.find(metric => metric.organizationId === selectedOrg.id);

      if (metrics) {
        return metrics.pricebookLicenseMetricsList.find(
          metric => metric.pricebookId === pricebook.pricebook.id,
        );
      }
    }
  }, [selectedOrg, orgMetrics, pricebook.pricebook.id]);

  const subscriptionDataCache =
    appStorage.retrieve<SubscriptionCompartments>(subscriptionStorageKey);
  const subscription =
    useObservable(subscriptionDataCache.observe$<SubscriptionData[]>('subscription')) ?? undefined;

  const currPrice = pricebook?.prices.find(price => price.intervalType !== IntervalTypeEnum.None);
  const price = (currPrice && currPrice.price / 100.0) ?? 0;

  const getLicenseChangeMessage = (values: any) => {
    const licenseChange = values.licenseCount - (licenseMetric?.purchasedLicenses ?? 0);
    if (licenseChange === 0) return "You haven't made any changes yet.";
    const action = licenseChange > 0 ? 'adding' : 'removing';
    return (
      <span>
        You're {action} <Bold>{Math.abs(licenseChange)}</Bold> {pluralize(licenseChange, 'license')}
        .
      </span>
    );
  };

  const getSubmitButtonText = (values: any) => {
    const licenseChange = values.licenseCount - (licenseMetric?.purchasedLicenses ?? 0);
    if (licenseChange === 0) return 'Purchase';
    const action = licenseChange > 0 ? 'Purchase' : 'Remove';
    return `${action} ${Math.abs(licenseChange)} ${pluralize(licenseChange, 'license')}`;
  };

  const onUpdateLicenses = async (values: any) => {
    try {
      setLoading(true);
      const licenseChange = values.licenseCount - (licenseMetric?.purchasedLicenses ?? 0);

      if (licenseChange < 0 && Math.abs(licenseChange) > (licenseMetric?.availableLicenses ?? 0)) {
        setToast(
          new Toast({
            message: "You're trying to remove too many licenses.",
            severity: 'error',
            open: true,
            autoHideDuration: 3000,
          }),
        );
        return;
      }

      const subscriptionData = {
        subscriptionId: activeSubscription?.id,
        amount: Math.abs(licenseChange),
      } as SubscriptionRest;

      if (licenseChange > 0) {
        await addLicenses.action(subscriptionData);
        const updatedSubscription = subscription?.find(x => x.id === activeSubscription?.id);

        if (updatedSubscription?.paymentFailed) {
          window.open(updatedSubscription.failedInvoiceUrl);
          onClose();
          return;
        }

        navigate('/congratsPurchase', {
          state: {
            quantity: licenseChange,
            chosenPackage: pricebookSelections[0],
          },
        });
      } else {
        await decreaseLicenses.action(subscriptionData);
      }

      setToast(
        new Toast({
          message:
            licenseChange > 0
              ? `${licenseChange} additional ${pluralize(licenseChange, 'license')} purchased!`
              : `${Math.abs(licenseChange)} ${pluralize(
                  Math.abs(licenseChange),
                  'license',
                )} removed!`,
          severity: 'success',
          open: true,
          autoHideDuration: 3000,
        }),
      );

      onClose();
    } catch (err) {
      console.error(err);
      setToast(new Toast({ severity: 'error', message: 'Failed to update licenses', open: true }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!subscription) {
      setActiveSubscription(undefined);
    }
    if (subscription) {
      // TODO this logic must change when there are more than 1 package to purchase additional licenses for
      const active = subscription?.find(x => x.productId === currPrice?.product.id);
      setActiveSubscription(active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricebook.prices, subscription]);

  useEffect(() => {
    const generateLicense = async () => {
      if (open && pricebook.pricebook.id) {
        try {
          const { data } = await licenseAgreementApi.generate(pricebook.pricebook.id);
          setInvoiceDate(data.invoiceDate);
        } catch (error) {
          console.error('Error generating license agreement:', error);
        }
      }
    };

    generateLicense();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pricebook.pricebook.id]);

  if (!licenseMetric) return null;

  return (
    <RightDrawer open={open} onClose={onClose}>
      <Form
        onSubmit={onUpdateLicenses}
        initialValues={{ licenseCount: licenseMetric.purchasedLicenses }}>
        {({ handleSubmit, values }) => {
          const licenseChange = values.licenseCount - (licenseMetric?.purchasedLicenses ?? 0);
          const isLicenseCountChanged = licenseChange !== 0;

          return (
            <Column>
              <Typography color='secondary.main' variant='p40'>
                Edit number of licenses
              </Typography>
              <Spacer height='xs' />
              <Field name='licenseCount'>
                {({ input }) => (
                  <LicenseAtAGlance
                    licenseMetrics={licenseMetric}
                    pricebookBundle={pricebook}
                    value={input.value}
                    onChange={input.onChange}
                  />
                )}
              </Field>
              <Spacer height='xs' />
              <Grid container flexDirection='column' alignItems='center'>
                {!isLicenseCountChanged && (
                  <Typography variant='p16' color='secondary.main'>
                    {licenseMetric.availableLicenses > 0 ? (
                      <>
                        You have <Bold>{licenseMetric.availableLicenses}</Bold> unused{' '}
                        {pluralize(licenseMetric.availableLicenses, 'license')}.
                      </>
                    ) : (
                      'All licenses have been claimed.'
                    )}
                  </Typography>
                )}
                <Typography variant='p16' color='secondary.main'>
                  {getLicenseChangeMessage(values)}
                </Typography>
              </Grid>
              <Spacer height='sm' />

              <Grid container display='inline-flex' flex={1} width='100%'>
                {isLicenseCountChanged && (
                  <LicensePaymentDetails
                    selectedPricebook={pricebook}
                    licenseQuantity={values.licenseCount}
                    addedLicenses={Math.max(licenseChange, 0)}
                    costPerLicense={price}
                    invoiceDate={invoiceDate}
                    hideSummary={licenseChange < 0}
                  />
                )}
              </Grid>

              <Spacer height='xs' />

              {isLicenseCountChanged && (
                <>
                  <Typography variant='p14' color='secondary'>
                    {licenseChange > 0
                      ? `*This amount may be prorated based on date of purchase. It will be automatically paid by the specified default payment method.`
                      : `*No refunds will be made for the time remaining on the current subscription period or for unused licenses.`}
                  </Typography>

                  <Spacer height='xxs' />

                  <Typography variant='p14' color='secondary'>
                    Please see our{' '}
                    <Link href={termsOfServiceDocs} target='_blank'>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link href='/cancellation-policy' target='_blank'>
                      Cancellation and Refund Policy
                    </Link>{' '}
                    for details.
                  </Typography>
                </>
              )}

              <Spacer height='xxl' />

              <Row style={{ justifyContent: 'space-between' }}>
                <Button onClick={onClose} variant='outlined' color='secondary' className='sm'>
                  Cancel
                </Button>
                {values.licenseCount !== licenseMetric.purchasedLicenses && (
                  <Button onClick={handleSubmit} color='primary'>
                    {getSubmitButtonText(values)}
                  </Button>
                )}
              </Row>
            </Column>
          );
        }}
      </Form>
    </RightDrawer>
  );
};
export default PurchaseLicensesDrawer;
